import React, { useState, useEffect } from 'react';
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import axios from 'axios';
import expense from "../expense.json";
import asset from "../asset.json";
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import { Table, Button } from "react-bootstrap";
import ToPeso from '../components/misc/ToPeso';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import LoadingSpinner from '../components/misc/LoadingSpinner';

const jsonDataExpense = expense;
const jsonDataAsset = asset;

function DailyProofsheet() {
    const [dateAdded, setDateAdded] = useState('');
    const [dateHeader, setDateHeader] = useState('');
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [credits, setCredits] = useState(0);
    const [debits, setDebits] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);

    const proofData = {
        cashOnHand: {code:"COHC", cat: "asset", value: jsonDataAsset.Asset["Cash"][0]},
        salariesAndWages: {code:"CFBSW", cat: "expense", value: jsonDataExpense.Expense["Compensation and Fringe Benefits"][0]},
        fringeBenefits: {code:"CFBFB", cat: "expense", value: jsonDataExpense.Expense["Compensation and Fringe Benefits"][1]},
        pagIbigExpense: {code:"PAGCEXP", cat: "expense", value: jsonDataExpense.Expense["SSS, PHIC, & HDMF Expense"][0]},
        PHICExpense: {code:"PHICCEXP", cat: "expense", value: jsonDataExpense.Expense["SSS, PHIC, & HDMF Expense"][1]},
        SSSExpense: {code:"SSSCEXP", cat: "expense", value: jsonDataExpense.Expense["SSS, PHIC, & HDMF Expense"][2]},
        TaxesAndLicenses: {code:"TAXLIC", cat: "expense", value: jsonDataExpense.Expense["Taxes and Licenses"][0]},
        rentExpense: {code:"RENEXP", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][0]},
        PTCT: {code:"PTCT", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][1]},
        PLW: {code:"PLW", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][2]},
        RepairsAndMaintenance: {code:"RAM", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][3]},
        MOPF: {code:"MOPF", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][7]},
        RepAndEntertainment: {code:"RAE", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][9]},
        CommAndHonorExpenses: {code:"CHE", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][10]},
        FuelAndLubricants: {code:"FUEL", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][13]},
        DonationsAndCharityContri: {code:"DCC", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][15]},
        StationAndOfficeSupplies: {code:"SSUD", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][17]},
        MiscExpenses: {code:"MISEXP", cat: "expense", value: jsonDataExpense.Expense["Administrative Expenses"][21]},
        DepreExpenseBldg: {code:"DPNEXPBLG", cat: "expense", value: jsonDataExpense.Expense["Depreciation/Amortization Expenses"][0]},
      };

      useEffect(() => {
        if (items.length > 0) {
            setCredits(0);
            setDebits(0);
            // Calculate debit and credit totals from all items
            const sumTotals = items.map((response) => sumData(response.data));
    
            // Aggregate the totals for debit and credit
            const aggregatedTotals = sumTotals.reduce(
                (acc, curr) => {
                    acc.debit += curr.debit;
                    acc.credit += curr.credit;
                    return acc;
                },
                { debit: 0, credit: 0 } // Initialize with 0
            );
    
            // Update state with aggregated totals
            setCredits((prev) => prev + aggregatedTotals.credit);
            setDebits((prev) => prev + aggregatedTotals.debit);
        }
    }, [items]); // Dependency on items ensures this runs only when items change    
    

    const handleSubmit = async (event) => {
        setShowSpinner(true);
        event.preventDefault();
    
        if (!dateAdded) {
            setError('Date Added is required.');
            return;
        }
    
        try {
            setError('');
    
            const assetItems = [];
            const expenseItems = [];
    
            // Categorize items based on asset or expense
            Object.values(proofData).forEach((entry) => {
                if (entry.cat === "asset") {
                    assetItems.push(entry.value.id);
                } else {
                    expenseItems.push(entry.value.id);
                }
            });
    
            // Prepare requests for asset and expense transactions
            const requests = [];
    
            if (assetItems.length > 0) {
                requests.push(
                    axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/asset-transactions/itemdate/multi`, {
                        params: {
                            item_ids: assetItems.join(','), // Send as a comma-separated string
                            date_added: dateAdded,
                        },
                    })
                );
            }
    
            if (expenseItems.length > 0) {
                requests.push(
                    axios.get(`${GLOBALVAR["SERVER_ROOT"]}/api/expense-transactions/itemdate/multi`, {
                        params: {
                            item_ids: expenseItems.join(','), // Send as a comma-separated string
                            date_added: dateAdded,
                        },
                    })
                );
            }
    
            // Execute both requests
            const responses = await Promise.all(requests);
    
            // Store the responses in the correct format
            setItems(responses);
    
            // Format date for display
            const dateObj = new Date(dateAdded);
            const formattedDate = dateObj.toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                year: 'numeric',
            });
            setDateHeader(formattedDate);
    
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("An error occurred while fetching data.");
        } finally {
            setShowSpinner(false);
        }
    };    

    const sumData = (data) => {
        // Validate data to ensure it's an array
        if (!Array.isArray(data)) {
            console.error('Invalid data passed to sumData. Expected an array.');
            return { debit: 0, credit: 0 }; // Return default values in case of error
        }
    
        // Calculate totals for debit and credit
        const totals = data.reduce((acc, curr) => {
            const amount = parseFloat(curr.amount) || 0; // Ensure `amount` is a valid number
            if (curr.credit) {
                acc.credit += amount;
            } else {
                acc.debit += amount;
            }
            return acc;
        }, { debit: 0, credit: 0 });
    
        return totals; // Return calculated totals
    };

    const handleExportPDF = async (data) => {
        setShowSpinner(true);
    
        const existingPdfBytes = await fetch("/pdftemplates/daily-proofsheet.pdf").then(res =>
            res.arrayBuffer()
        );
    
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
        const pages = pdfDoc.getPages();
        const firstPage = pages[0];
    
        const centerText = (str) => {
            const pageWidth = firstPage.getWidth();
            const textWidth = helveticaFont.widthOfTextAtSize(str, 9);
            return (pageWidth - textWidth) / 2;
        };
    
        // Get formatted date and time
        const today = new Date();
        const dayToday = today.getDate();
        const monthToday = today.toLocaleString('default', { month: 'long' });
        const yearToday = today.getFullYear();
        let hours = today.getHours();
        const minutes = today.getMinutes().toString().padStart(2, '0');
        const seconds = today.getSeconds().toString().padStart(2, '0');
        const amPm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
    
        const dateTimeString = `${monthToday} ${dayToday}, ${yearToday} ${hours}:${minutes}:${seconds} ${amPm}`;
    
        firstPage.drawText(dateTimeString, { x: 90, y: 167, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
        firstPage.drawText(`${dateHeader}`, { x: centerText(dateHeader), y: 687, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
    
        // Define y-coordinates
        const coordY = [621, 585, 572, 535, 523, 511, 499, 461, 449, 437, 425, 413, 401, 389, 377, 365, 353, 341, 303];
    
        // Ensure proofData is an array and has entries
        if (proofData && Object.keys(proofData).length > 0) {
            Object.values(proofData).forEach((entry, index) => {
                if (index >= coordY.length) return; // Avoid exceeding coordinate array length
                
                // Find matching response in items
                const response = items.find(res =>
                    res.config.params.item_ids.split(',').map(id => parseInt(id)).includes(entry.value.id)
                );
    
                const data = response?.data.filter(d => d.item_id === entry.value.id) || [];
                const totalDebit = sumData(data)?.debit || 0;
                const totalCredit = sumData(data)?.credit || 0;
    
                firstPage.drawText(`${totalDebit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { 
                    x: 376, y: coordY[index], size: 9, font: helveticaFont, color: rgb(0, 0, 0) 
                });
    
                firstPage.drawText(`${totalCredit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { 
                    x: 482, y: coordY[index], size: 9, font: helveticaFont, color: rgb(0, 0, 0) 
                });
            });
    
            firstPage.drawText(`${debits.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { 
                x: 376, y: 278, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) 
            });
    
            firstPage.drawText(`${credits.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { 
                x: 482, y: 278, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) 
            });
        }
    
        const dateObj = new Date(dateHeader);
        const timestamp = Math.floor(dateObj.getTime() / 1000);
    
        const pdfBytes = await pdfDoc.save();
        saveAs(new Blob([pdfBytes]), `Daily_Proofsheet_${timestamp}.pdf`);
    
        setShowSpinner(false);
    };    
        
 
    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <NavMain slug="daily-proofsheet" parent="3" />
            <HeaderMain slug="daily-proofsheet" />
            <div className="main-content financials">
                <div className="container">
                    <h4>Retrieve Daily ProofSheet</h4>
                    <form onSubmit={handleSubmit} className="mb-4">
                        <div className="mb-3">
                            <label htmlFor="dateAdded" className="form-label">Date Added</label>
                            <input
                                type="date"
                                className="form-control"
                                id="dateAdded"
                                value={dateAdded}
                                onChange={(e) => setDateAdded(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary">Retrieve</button>
                    </form>
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="date-selected">{dateHeader}</div>
                    {
                        items && (
                            <>
                                <Table className="list-container table table-hover" responsive>
                                    <thead>
                                        <tr>
                                            <th className="list-header-item">GL Code</th>
                                            <th className="list-header-item">GL Account</th>
                                            <th className="list-header-item">DEBIT</th>
                                            <th className="list-header-item">CREDIT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.values(proofData).map((entry, index) => {
                                            // Find the corresponding response in items
                                            const response = items.find(res => {
                                                const itemIds = res.config.params.item_ids.split(',').map(id => parseInt(id, 10)); // Convert string to array of numbers
                                                return itemIds.includes(entry.value.id);
                                            });

                                            // Extract and sum debit and credit
                                            const data = response?.data.filter(d => d.item_id === entry.value.id) || [];
                                            const totalDebit = sumData(data)?.debit || 0;
                                            const totalCredit = sumData(data)?.credit || 0;
                                            return (
                                                <tr key={index} className="list-body">
                                                    <td className="list-body-item">{entry.code}</td> 
                                                    <td className="list-body-item">{entry.value.label}</td> 
                                                    <td className="list-body-item"><ToPeso amount={totalDebit} /></td>
                                                    <td className="list-body-item"><ToPeso amount={totalCredit} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="list-body">
                                            <td className="list-body-item">Summary :</td>
                                            <td className="list-body-item">&nbsp;</td>
                                            <td className="list-body-item"><ToPeso amount={debits} /></td>
                                            <td className="list-body-item"><ToPeso amount={credits} /></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                                <Button variant="primary" onClick={() => handleExportPDF(items)}>
                                    Export PDF
                                </Button>
                            </>
                        )
                    }

                </div>
            </div>
        </>
    );
}

export default DailyProofsheet;